<template>
  <div class="content">

    <!-- Étape 1 : Type de commande -->
    <v-card outlined class="mb-4">
      <v-card-title style="font-size: 14px">Étape 1 : Type de commande</v-card-title>
      <v-card-text>
        <v-radio-group v-model="orderType" mandatory>
          <v-radio
            label="Bon de commande financé SEGUR Vague 2"
            value="withinSegur"
            color="#e2007a"
          ></v-radio>
          <v-radio
            label="Pack SEGUR V2 hors financement"
            value="outsideSegur"
            color="grey"
            disabled
          ></v-radio>
        </v-radio-group>
        <div style="font-size: 12px; color: #999; margin-top: -8px; padding-left: 32px">
          Disponible à la fin de la campagne SEGUR Vague 2 (16 décembre 2026)
        </div>
      </v-card-text>
    </v-card>

    <!-- Étape 2 : Informations du praticien -->
    <v-card outlined class="mb-4">
      <v-card-title style="font-size: 14px">Étape 2 : Informations du praticien</v-card-title>
      <v-card-text>
        <v-radio-group v-model="commandePour" row mandatory>
          <v-radio label="Individuel (libéral)" value="LIBERAL" color="#e2007a"></v-radio>
          <v-radio label="Structure (MSP)" value="STRUCTURE" color="#e2007a"></v-radio>
        </v-radio-group>

        <!-- RPPS + Licence -->
        <v-row dense>
          <v-col cols="12" sm="6">
            <v-text-field
              v-model="rpps"
              label="RPPS"
              maxlength="11"
              :loading="rppsLoading"
              :append-icon="rppsValidIcon"
              :rules="[rules.rppsFormat]"
              @input="onRppsInput"
              outlined
              dense
            ></v-text-field>
          </v-col>
          <v-col cols="12" sm="6">
            <div style="display: flex; align-items: flex-start; gap: 8px">
              <v-select
                v-if="!manualLicence"
                v-model="selectedLicence"
                :items="licences"
                item-text="display"
                item-value="licenceID"
                :label="licenceLabel"
                :disabled="!rppsValid"
                :loading="rppsLoading"
                outlined
                dense
                style="flex: 1"
              ></v-select>
              <v-text-field
                v-if="manualLicence"
                v-model="manualLicenceId"
                :label="licenceLabel + ' (saisie manuelle)'"
                :disabled="!rppsValid"
                :append-icon="manualLicenceValidIcon"
                @input="checkManualLicence"
                outlined
                dense
                style="flex: 1"
              ></v-text-field>
              <v-btn
                icon
                small
                color="#e2007a"
                @click="manualLicence = !manualLicence"
                :disabled="!rppsValid"
                :title="manualLicence ? 'Revenir à la sélection' : 'Saisie manuelle'"
                style="margin-top: 4px"
              >
                <v-icon small>{{ manualLicence ? 'mdi-close' : 'mdi-pencil' }}</v-icon>
              </v-btn>
            </div>
          </v-col>
        </v-row>

        <!-- Genre + Nom + Prénom -->
        <v-row dense>
          <v-col cols="12" sm="3">
            <v-select
              v-model="genre"
              :items="[{ text: 'Madame', value: 'Madame' }, { text: 'Monsieur', value: 'Monsieur' }]"
              label="Genre"
              outlined
              dense
            ></v-select>
          </v-col>
          <v-col cols="12" sm="4">
            <v-text-field v-model="nom" label="Nom" outlined dense></v-text-field>
          </v-col>
          <v-col cols="12" sm="5">
            <v-text-field v-model="prenom" label="Prénom" outlined dense></v-text-field>
          </v-col>
        </v-row>

        <!-- Téléphone + Email -->
        <v-row dense>
          <v-col cols="12" sm="6">
            <v-text-field v-model="telephone" label="Téléphone" outlined dense></v-text-field>
          </v-col>
          <v-col cols="12" sm="6">
            <v-text-field v-model="email" label="Email" type="email" outlined dense></v-text-field>
          </v-col>
        </v-row>

        <!-- Adresse + CP + Ville -->
        <v-row dense>
          <v-col cols="12" sm="6">
            <v-text-field v-model="adresse" label="Adresse" outlined dense></v-text-field>
          </v-col>
          <v-col cols="12" sm="3">
            <v-text-field v-model="codePostal" label="Code postal" maxlength="5" outlined dense></v-text-field>
          </v-col>
          <v-col cols="12" sm="3">
            <v-text-field v-model="ville" label="Ville" outlined dense></v-text-field>
          </v-col>
        </v-row>

        <!-- Structure : FINESS juridique + FINESSGEO etablissement + Raison sociale + Effectif + Tranche -->
        <template v-if="commandePour === 'STRUCTURE'">
          <v-divider class="my-3"></v-divider>
          <v-row>
            <v-col cols="12" sm="4">
              <v-text-field
                v-model="finess"
                label="FINESS juridique"
                maxlength="9"
                :loading="finessLoading"
                hint="9 chiffres - FINESS de l'entite juridique"
                persistent-hint
                outlined
                dense
              ></v-text-field>
            </v-col>
            <v-col cols="12" sm="4" v-if="showFinessGeo">
              <!-- FINESSGEO : visible pour SaaS, masque Prokov si MSPList a fourni la valeur -->
              <v-text-field
                v-model="finessGeo"
                label="FINESS geographique (etablissement)"
                maxlength="9"
                :rules="[v => !v || /^\d{9}$/.test(v) || 'FINESSGEO : 9 chiffres']"
                hint="9 chiffres - sur le contrat ANS, ligne 'etablissement' (different du FINESS juridique)"
                persistent-hint
                outlined
                dense
                required
              ></v-text-field>
            </v-col>
            <v-col cols="12" sm="4">
              <v-text-field
                v-model.number="effectif"
                label="Effectif"
                type="number"
                min="1"
                :readonly="mspFound"
                :hint="!mspFound && mspChecked ? 'Optionnel : pour information uniquement' : ''"
                persistent-hint
                outlined
                dense
              ></v-text-field>
            </v-col>
          </v-row>
          <v-row dense>
            <v-col cols="12" sm="8">
              <!-- raisonSociale editable (meme si MSPList l'a fourni, peut etre corrigee) -->
              <v-text-field
                v-model="mspRaisonSociale"
                label="Raison sociale de la structure"
                :hint="mspFound && mspRaisonSocialeFromLookup ? 'Valeur Base ANS (modifiable)' : 'Saisie manuelle'"
                persistent-hint
                outlined
                dense
                required
              ></v-text-field>
            </v-col>
            <v-col cols="12" sm="4">
              <!-- Tranche en lecture seule si Base ANS, sinon selecteur obligatoire -->
              <v-text-field
                v-if="mspFound"
                v-model="tranche"
                label="Tranche"
                readonly
                outlined
                dense
              ></v-text-field>
              <v-select
                v-else
                v-model="tranche"
                :items="['A', 'B', 'C', 'D', 'E']"
                label="Tranche (Base ANS)"
                hint="À demander à la structure ou à consulter sur la Base de référence ANS"
                persistent-hint
                outlined
                dense
              ></v-select>
            </v-col>
          </v-row>
          <v-chip
            v-if="mspFound"
            small
            color="success"
            outlined
            class="mt-1"
          >
            <v-icon small left>mdi-check-circle</v-icon>
            Trouvé via Base ANS : {{ mspRaisonSociale || '(raison sociale inconnue)' }}
          </v-chip>
          <v-chip
            v-else-if="mspChecked"
            small
            color="orange"
            outlined
            class="mt-1"
          >
            <v-icon small left>mdi-pencil-outline</v-icon>
            FINESS inconnu en Base ANS : saisie manuelle requise (FINESSGEO + raison sociale + tranche).
          </v-chip>
        </template>
      </v-card-text>
    </v-card>

    <!-- Étape 3 : Récapitulatif et montants -->
    <v-card outlined class="mb-4">
      <v-card-title style="font-size: 14px">Étape 3 : Récapitulatif et montants</v-card-title>
      <v-card-text>
        <v-row align="center">
          <v-col cols="12" sm="4">
            <v-menu
              v-model="dateMenu"
              :close-on-content-click="false"
              transition="scale-transition"
              offset-y
              max-width="290px"
              min-width="auto"
            >
              <template v-slot:activator="{ on, attrs }">
                <v-text-field
                  v-model="dateSignature"
                  label="Date de signature"
                  prepend-icon="mdi-calendar"
                  readonly
                  outlined
                  dense
                  v-bind="attrs"
                  v-on="on"
                ></v-text-field>
              </template>
              <v-date-picker
                v-model="dateSignature"
                no-title
                @input="dateMenu = false"
              ></v-date-picker>
            </v-menu>
          </v-col>
          <v-col cols="12" sm="8">
            <div style="display: flex; align-items: center; gap: 12px">
              <v-select
                v-model="typePrestation"
                :items="typePrestationItems"
                label="Type de prestation"
                outlined
                dense
                :disabled="!rppsValid || existingBDC != null"
                @change="computePricingPreview"
                style="flex: 1; max-width: 320px"
              ></v-select>
              <v-chip
                v-if="typePrestation"
                :color="typePrestation === 'V2' ? '#e2007a' : '#ff9800'"
                text-color="white"
                small
                style="margin-bottom: 22px"
              >
                {{ typePrestation === 'V2' ? 'Déjà équipé V1' : 'Jamais équipé' }}
              </v-chip>
              <span v-else style="font-size: 12px; color: #999; margin-bottom: 22px">
                Auto-détecté via le RPPS
              </span>
            </div>
          </v-col>
        </v-row>

        <!-- Mini-cards montants -->
        <v-row v-if="pricing" dense class="mt-2">
          <v-col cols="12" sm="4" class="d-flex">
            <v-card outlined style="text-align: center; padding: 16px; width: 100%">
              <div style="font-size: 12px; color: #888; text-transform: uppercase; letter-spacing: 0.5px">Montant HT</div>
              <div style="font-size: 28px; font-weight: bold; color: #e2007a; margin-top: 8px">{{ formatEur(pricing.montantHT) }}</div>
            </v-card>
          </v-col>
          <v-col cols="12" sm="4" class="d-flex">
            <v-card outlined style="text-align: center; padding: 16px; width: 100%">
              <div style="font-size: 12px; color: #888; text-transform: uppercase; letter-spacing: 0.5px">TVA {{ pricing.tauxTVA }}%</div>
              <div style="font-size: 28px; font-weight: bold; color: #333; margin-top: 8px">{{ formatEur(pricing.montantTTC) }}</div>
              <div style="font-size: 11px; color: #888">TTC</div>
            </v-card>
          </v-col>
          <v-col cols="12" sm="4" class="d-flex">
            <v-card outlined style="text-align: center; padding: 16px; width: 100%">
              <div style="font-size: 12px; color: #888; text-transform: uppercase; letter-spacing: 0.5px">Avance / Solde</div>
              <div style="font-size: 18px; font-weight: bold; color: #4caf50; margin-top: 8px">40% : {{ formatEur(pricing.avanceTTC) }}</div>
              <div style="font-size: 14px; color: #ff9800; margin-top: 2px">60% : {{ formatEur(pricing.soldeTTC) }}</div>
            </v-card>
          </v-col>
        </v-row>

        <!-- BDC existant sans PDF : message d'info -->
        <v-alert v-if="existingBDC && !existingBDC.hasPdf" type="warning" outlined class="mt-4">
          Un BDC V2 existe déjà pour ce praticien (n° {{ existingBDC.numeroEditeur }}).
          Rendez-vous à l'étape 4 ci-dessous pour uploader le PDF signé.
        </v-alert>

        <!-- BDC existant avec PDF : tout est complet -->
        <v-alert v-else-if="existingBDC && existingBDC.hasPdf" type="success" outlined class="mt-4">
          Ce praticien a déjà un BDC V2 complet (n° {{ existingBDC.numeroEditeur }}, PDF uploadé).
        </v-alert>

        <!-- Alert backend 400 types (persistant, pas snackbar) -->
        <v-alert
          v-if="backendError"
          type="error"
          outlined
          dismissible
          class="mt-4"
          @input="backendError = null"
        >
          <strong>[{{ backendError.code }}]</strong> {{ backendError.msg }}
        </v-alert>

        <!-- Pas de BDC : bouton création avec tooltip champs manquants -->
        <div v-else class="mt-4">
          <v-tooltip bottom :disabled="structureComplete && rppsValid">
            <template #activator="{ on }">
              <div v-on="on" style="display: inline-block">
                <v-btn
                  color="#e2007a"
                  dark
                  large
                  :loading="creating"
                  :disabled="!rppsValid || creating || !structureComplete"
                  @click="createBDC"
                >
                  Créer le bon de commande
                </v-btn>
              </div>
            </template>
            <span v-if="!rppsValid">RPPS invalide ou manquant</span>
            <span v-else-if="structureMissingFields.length">
              Champs manquants : {{ structureMissingFields.join(', ') }}
            </span>
          </v-tooltip>
        </div>
      </v-card-text>
    </v-card>

    <!-- Étape 4 : Upload du BDC signé -->
    <v-card v-if="numeroEditeur" outlined class="mb-4">
      <v-card-title style="font-size: 14px">Étape 4 : Upload du BDC signé</v-card-title>
      <v-card-text>
        <v-alert type="info" outlined class="mb-4">
          Le numéro éditeur <strong>{{ numeroEditeur }}</strong> a été généré.
          Inscrivez-le sur le PDF, faites-le signer par le praticien, puis uploadez le scan ci-dessous.
        </v-alert>

        <v-text-field
          :value="numeroEditeur"
          label="Numero editeur"
          readonly
          outlined
          dense
          append-icon="mdi-content-copy"
          @click:append="copyNumeroEditeur"
        ></v-text-field>

        <v-file-input
          v-model="pdfFile"
          label="PDF du BDC signe"
          accept="application/pdf"
          :rules="[rules.pdfSize]"
          outlined
          dense
          prepend-icon="mdi-file-pdf-box"
          class="mt-2"
        ></v-file-input>

        <v-btn
          color="#e2007a"
          dark
          :loading="uploading"
          :disabled="!pdfFile || uploading"
          @click="sendPdf"
          class="mt-2"
        >
          Envoyer le PDF
        </v-btn>
      </v-card-text>
    </v-card>

    <!-- Snackbar -->
    <v-snackbar v-model="snackbar" :color="snackbarColor" :timeout="4000">
      {{ snackbarText }}
      <template v-slot:action="{ attrs }">
        <v-btn text v-bind="attrs" @click="snackbar = false">Fermer</v-btn>
      </template>
    </v-snackbar>

  </div>
</template>

<script>
import axios from "axios"
import store from "@/store"
import server from "@/store/server"

const API_BASE = server.url

/**
 * Grille tarifaire SEGUR Vague 2.
 * [SEGUR-SPEC] Montants imposes par AF-MDV-LGC-Va2 Annexe 3.
 */
const MONTANTS = {
  V2: { LIBERAL: 376.25, STRUCTURE: { A: 2633.75, B: 4515, C: 6396.25, D: 8277.50, E: 10158.75 } },
  V1V12: { LIBERAL: 483.75, STRUCTURE: { A: 3386.25, B: 5805, C: 8223.75, D: 10642.50, E: 13061.25 } }
}

/**
 * Calcul du taux de TVA selon le code postal (departements d'outre-mer).
 * [SEGUR-SPEC] Guyane/Saint-Pierre/Mayotte = 0%, Guadeloupe/Martinique/Reunion = 8.5%, reste = 20%.
 */
function getTauxTVA(cp) {
  if (!cp || cp.length < 3) return 20
  const p = cp.substring(0, 3)
  if (p === '973' || p === '975' || p === '976') return 0
  if (p === '971' || p === '972' || p === '974') return 8.5
  return 20
}

function round2(val) {
  return Math.round((val + Number.EPSILON) * 100) / 100
}

export default {
  name: "SegurBon",
  data() {
    return {
      orderType: 'withinSegur',
      commandePour: 'LIBERAL',
      rpps: '',
      rppsValid: null,
      rppsLoading: false,
      licences: [],
      selectedLicence: null,
      manualLicence: false,
      manualLicenceId: '',
      manualLicenceValid: null,
      genre: 'Madame',
      nom: '',
      prenom: '',
      telephone: '',
      email: '',
      adresse: '',
      codePostal: '',
      ville: '',
      finess: '',
      finessGeo: '',
      effectif: null,
      tranche: null,
      finessLoading: false,
      mspChecked: false,
      mspFound: false,
      mspRaisonSociale: '',
      mspRaisonSocialeFromLookup: false,
      backendError: null,
      dateMenu: false,
      dateSignature: new Date().toISOString().substr(0, 10),
      typePrestation: null,
      pricing: null,
      existingBDC: null,
      numeroEditeur: null,
      pdfFile: null,
      creating: false,
      uploading: false,
      snackbar: false,
      snackbarText: '',
      snackbarColor: 'success',
      residentId: null,
      rules: {
        rppsFormat: v => !v || /^\d{0,11}$/.test(v) || 'Le RPPS doit contenir 11 chiffres',
        pdfSize: v => !v || v.size <= 3 * 1024 * 1024 || 'Le fichier ne doit pas depasser 3 Mo'
      }
    }
  },
  created() {
    // Tokens non-reactifs (pas de re-render, usage interne pour race control)
    this._finessLookupToken = 0
    this._rppsLookupToken = 0
  },
  computed: {
    rppsValidIcon() {
      if (this.rppsLoading) return ''
      if (this.rppsValid === true) return 'mdi-check-circle'
      if (this.rppsValid === false) return 'mdi-close-circle'
      return ''
    },
    manualLicenceValidIcon() {
      if (this.manualLicenceValid === true) return 'mdi-check-circle'
      if (this.manualLicenceValid === false) return 'mdi-close-circle'
      return ''
    },
    typePrestationItems() {
      return [
        { text: 'V2 (déjà équipé V1)', value: 'V2' },
        { text: 'V1V12 (jamais équipé)', value: 'V1V12' },
      ]
    },
    // Libelle du champ licence par partenaire (Licence MS4, Licence Easy Care, etc.).
    licenceLabel() {
      return localStorage.getItem('partnerLicenceLabel') || 'Licence'
    },
    // Affichage FINESSGEO : visible pour tous sauf Prokov en mode mspFound
    // (Prokov a MSPList peuplee qui fournit FINESSGEO automatiquement).
    showFinessGeo() {
      if (this.commandePour !== 'STRUCTURE') return false
      const partnerCode = localStorage.getItem('partnerCode') || 'prokov'
      if (partnerCode === 'prokov' && this.mspFound && this.finessGeo) return false
      return true
    },
    // Validation stricte STRUCTURE : FINESS + FINESSGEO + raisonSociale + tranche
    structureComplete() {
      if (this.commandePour !== 'STRUCTURE') return true
      if (this.finessLoading) return false
      if (!this.finess || !/^\d{9}$/.test(this.finess)) return false
      if (this.showFinessGeo && (!this.finessGeo || !/^\d{9}$/.test(this.finessGeo))) return false
      if (!this.mspRaisonSociale || !this.mspRaisonSociale.trim()) return false
      if (!['A', 'B', 'C', 'D', 'E'].includes(this.tranche)) return false
      if (!this.mspFound && !this.mspChecked) return false
      return true
    },
    // Liste des champs manquants pour tooltip explicite
    structureMissingFields() {
      if (this.commandePour !== 'STRUCTURE') return []
      const missing = []
      if (!this.finess || !/^\d{9}$/.test(this.finess)) missing.push('FINESS')
      if (this.showFinessGeo && (!this.finessGeo || !/^\d{9}$/.test(this.finessGeo))) missing.push('FINESSGEO')
      if (!this.mspRaisonSociale || !this.mspRaisonSociale.trim()) missing.push('Raison sociale')
      if (!['A', 'B', 'C', 'D', 'E'].includes(this.tranche)) missing.push('Tranche')
      return missing
    }
  },
  watch: {
    commandePour(newVal) {
      // Reset champs STRUCTURE quand on bascule vers LIBERAL
      if (newVal === 'LIBERAL') {
        this.finess = ''
        this.finessGeo = ''
        this.effectif = null
        this.tranche = null
        this.mspChecked = false
        this.mspFound = false
        this.mspRaisonSociale = ''
        this.mspRaisonSocialeFromLookup = false
      }
      this.computePricingPreview()
    },
    codePostal(val) {
      if (val && val.length === 5) {
        this.computePricingPreview()
      }
    },
    tranche() {
      this.computePricingPreview()
    },
    finess(val) {
      // Lookup MSPList des que 9 chiffres sont saisis. Si moins, on reset l'etat.
      if (val && /^\d{9}$/.test(val)) {
        this.lookupMspByFiness(val)
      } else {
        this.mspChecked = false
        this.mspFound = false
        this.mspRaisonSociale = ''
        this.effectif = null
        this.tranche = null
      }
    }
  },
  methods: {
    /**
     * Reagit a chaque saisie dans le champ RPPS.
     * Quand 11 chiffres valides, lance le chargement en cascade :
     * resident SEGUR -> licences -> fallback eeDb -> detection typePrestation -> pricing.
     */
    async onRppsInput() {
      this.rpps = this.rpps.replace(/\D/g, '')
      if (this.rpps.length > 11) this.rpps = this.rpps.slice(0, 11)

      if (/^\d{11}$/.test(this.rpps)) {
        this.rppsLoading = true
        this.rppsValid = null
        try {
          await this.fetchSegurResident(this.rpps)
          await this.fetchLicencesByRPPS(this.rpps)
          await this.fetchUserFromEeDb(this.rpps)
          await this.detectTypePrestation(this.rpps)
          this.computePricingPreview()
          this.rppsValid = true
        } catch {
          this.rppsValid = false
          this.showSnackbar('Erreur lors du chargement des donnees RPPS', 'error')
        } finally {
          this.rppsLoading = false
        }
      } else {
        this.rppsValid = this.rpps.length > 0 ? false : null
        this.resetForm()
      }
    },

    /**
     * Charge les donnees du resident SEGUR depuis le store Vuex.
     * Prefixe le RPPS avec "8" (convention SEGUR).
     */
    async fetchSegurResident(rpps) {
      const rppsWithPrefix = '8' + rpps
      try {
        await this.$store.dispatch('segur/getResidentSegurIDbyRPPS', rppsWithPrefix)
        const data = this.$store.getters['segur/getResidentID']
        if (data && data.data) {
          const r = data.data
          if (r.INFOS_PORTEUR_NomPatronym) {
            this.nom = r.INFOS_PORTEUR_NomPatronym
            this.prenom = r.INFOS_PORTEUR_PrenomUsuel || ''
            this.genre = r.INFOS_PORTEUR_CodeCivilite === 31 ? 'Monsieur' : 'Madame'
            this.telephone = r.phone || ''
            this.email = r.email || ''
            this.ville = r.city || ''
            this.codePostal = r.zip || ''
            this.adresse = r.address || ''
          }
        }
      } catch {
        // Pas de donnee resident, le fallback eeDb prendra le relais
      }
    },

    /**
     * Charge les licences MS4 associees au RPPS.
     * Auto-selectionne si une seule licence trouvee.
     */
    async fetchLicencesByRPPS(rpps) {
      try {
        const res = await axios.get(
          `${API_BASE}${server.urls.segur}/licences-by-rpps/${rpps}`,
          { headers: server.getHeader() }
        )
        this.licences = (res.data?.data || []).map(l => ({
          ...l,
          display: `${l.licenceID} — ${l.customer || ''}`
        }))
        if (this.licences.length === 1) {
          this.selectedLicence = this.licences[0].licenceID
        }
      } catch {
        this.licences = []
      }
    },

    /**
     * Fallback : charge les donnees utilisateur depuis eeDb si le resident SEGUR n'a pas ete trouve.
     * Ne remplit que les champs encore vides.
     */
    async fetchUserFromEeDb(rpps) {
      if (this.nom) return
      try {
        const res = await axios.get(
          `${API_BASE}${server.urls.segur}/user-by-rpps/${rpps}`,
          { headers: server.getHeader() }
        )
        const user = res.data?.data
        if (user) {
          this.nom = this.nom || user.familyname || ''
          this.prenom = this.prenom || user.givenname || ''
          this.telephone = this.telephone || user.phone || ''
          this.email = this.email || user.email || ''
          this.ville = this.ville || user.city || ''
          this.codePostal = this.codePostal || user.zip || ''
          this.adresse = this.adresse || user.address || ''
          if (!this.nom) {
            this.genre = user.sex === 1 ? 'Monsieur' : 'Madame'
          }
        }
      } catch {
        // Fallback silencieux
      }

      // 3eme fallback : annuaire RPPS ANS
      if (!this.nom) {
        await this.fetchFromRppsDb(rpps)
      }
    },

    /**
     * Dernier fallback : interroge l'annuaire RPPS de l'ANS.
     */
    async fetchFromRppsDb(rpps) {
      try {
        const res = await axios.get(
          `${API_BASE}/rpps/lookup/${rpps}`,
          { headers: server.getHeader() }
        )
        const data = res.data?.data
        if (data) {
          this.nom = data.nom || ''
          this.prenom = data.prenom || ''
          this.telephone = data.phone || ''
          this.email = data.email || ''
          this.ville = data.city || ''
          this.codePostal = data.zip || ''
          this.adresse = data.address || ''
          this.genre = (data.civilite === 'Mme' || data.civilite === 'Madame') ? 'Madame' : 'Monsieur'
        }
      } catch {
        // Annuaire RPPS indisponible
      }
    },

    /**
     * Detecte le type de prestation V2 ou V1V12 pour ce RPPS.
     * V2 = deja equipe en V1, V1V12 = primo-equipement.
     */
    async detectTypePrestation(rpps) {
      try {
        const res = await axios.get(
          `${API_BASE}${server.urls.segur}/v2/detect-type/${rpps}`,
          { headers: server.getHeader() }
        )
        this.typePrestation = res.data?.data?.typePrestation || 'V1V12'

        // Si un BDC V2 existe deja, reprendre le flow au bon endroit
        const existing = res.data?.data?.existingV2BDC
        if (existing) {
          this.existingBDC = existing
          this.numeroEditeur = existing.numeroEditeur
          if (existing.hasPdf) {
            this.showSnackbar('Ce praticien a déjà un BDC V2 complet (PDF uploadé)', 'info')
          } else {
            this.showSnackbar('BDC V2 existant (n° ' + existing.numeroEditeur + '), PDF en attente', 'warning')
          }
        } else {
          this.existingBDC = null
          this.numeroEditeur = null
        }
      } catch {
        this.typePrestation = 'V1V12'
        this.existingBDC = null
      }
    },

    /**
     * Calcul local des montants (HT, TVA, TTC, avance 40%, solde 60%).
     * [SEGUR-SPEC] Grille de montants AF-MDV-LGC-Va2 Annexe 3.
     */
    computePricingPreview() {
      if (!this.typePrestation) {
        this.pricing = null
        return
      }

      const grid = MONTANTS[this.typePrestation]
      if (!grid) {
        this.pricing = null
        return
      }

      let montantHT
      if (this.commandePour === 'STRUCTURE') {
        // Sans tranche valide on ne peut PAS retomber sur le tarif liberal : ce serait faux.
        if (!this.tranche) {
          this.pricing = null
          return
        }
        montantHT = grid.STRUCTURE[this.tranche]
      } else {
        montantHT = grid.LIBERAL
      }

      if (!montantHT) {
        this.pricing = null
        return
      }

      const tauxTVA = getTauxTVA(this.codePostal)
      const montantTTC = Math.round(montantHT * (100 + tauxTVA)) / 100
      const avanceHT = round2(montantHT * 0.4)
      const soldeHT = round2(montantHT * 0.6)
      const avanceTTC = Math.round(avanceHT * (100 + tauxTVA)) / 100
      const soldeTTC = Math.round(soldeHT * (100 + tauxTVA)) / 100

      this.pricing = { montantHT, tauxTVA, montantTTC, avanceHT, soldeHT, avanceTTC, soldeTTC }
    },

    /**
     * Cree le bon de commande via POST /segur/createorderform.
     * Envoie l'objet au format attendu par le backend V2.
     */
    async createBDC() {
      this.creating = true
      this.backendError = null
      try {
        const obj = {
          resident: {
            RPPS: this.rpps,
            INFOS_PORTEUR_PrenomUsuel: this.prenom,
            INFOS_PORTEUR_NomPatronym: this.nom,
            INFOS_PORTEUR_CodeCivilite: this.genre === 'Madame' ? 22 : 31,
            INFOS_PS2_CodeProfession: 10,
            phone: this.telephone,
            email: this.email,
            apps: localStorage.getItem('partnerApps') || 'MS4',
            city: this.ville,
            zip: this.codePostal,
            address: this.adresse
          },
          msp: this.commandePour === 'STRUCTURE'
            ? {
                FINESS: this.finess,
                // FINESSGEO : on envoie tel quel. Le backend valide ^\d{9}$ et rejette
                // si absent (fail-closed, pas de fallback FINESSGEO=FINESS).
                FINESSGEO: this.finessGeo || '',
                effectifRef: this.effectif || null,
                raisonSociale: this.mspRaisonSociale || '',
                city: this.ville,
                zip: this.codePostal,
                address: this.adresse,
              }
            : undefined,
          orderForm: {
            createdAt: this.dateSignature,
            licence: this.manualLicence ? this.manualLicenceId : this.selectedLicence,
            commandePour: this.commandePour,
            tranche: this.tranche
          },
          isOutsideSegur: false,
          wave: 'V2'
        }

        const res = await axios.post(
          `${API_BASE}${server.urls.segur}/createorderform`,
          { obj },
          { headers: server.getHeader() }
        )

        if (res.data?.data?.error) {
          // Erreur 400 type venant du backend (code dans data.code)
          this.backendError = {
            code: res.data.data.code || 'ERROR',
            msg: res.data.data.error
          }
          return
        }

        const orderForm = res.data?.data?.orderForm || res.data?.data
        this.numeroEditeur = orderForm?.numeroEditeur
        this.showSnackbar('BDC créé avec succès, numéro éditeur : ' + this.numeroEditeur, 'success')
      } catch (err) {
        // Lecture du code erreur typé backend (400 avec data.code)
        const code = err.response?.data?.data?.code
        const msg = err.response?.data?.data?.error || err.response?.data?.message || err.message || 'Erreur lors de la creation du BDC'
        this.backendError = { code: code || 'ERROR', msg }
      } finally {
        this.creating = false
      }
    },

    /**
     * Upload du PDF signe en base64 via POST /segur/createorderformpdf.
     * Lit le fichier depuis le v-file-input, convertit en base64, puis envoie.
     */
    async sendPdf() {
      if (!this.pdfFile) {
        this.showSnackbar('Veuillez selectionner un fichier PDF', 'warning')
        return
      }
      if (this.pdfFile.size > 3 * 1024 * 1024) {
        this.showSnackbar('Le fichier ne doit pas depasser 3 Mo', 'warning')
        return
      }
      if (!this.numeroEditeur) {
        this.showSnackbar('Aucun numero editeur', 'warning')
        return
      }

      this.uploading = true
      try {
        const base64 = await this.fileToBase64(this.pdfFile)
        await axios.post(
          `${API_BASE}${server.urls.segur}/createorderformpdf`,
          { obj: { pdf: base64, numeroEditeur: this.numeroEditeur } },
          { headers: server.getHeader() }
        )
        this.showSnackbar('PDF envoye avec succes', 'success')
        this.pdfFile = null
      } catch (err) {
        const msg = err.response?.data?.message || err.message || 'Erreur lors de l\'envoi du PDF'
        this.showSnackbar(msg, 'error')
      } finally {
        this.uploading = false
      }
    },

    /**
     * Convertit un objet File en base64 (sans le prefixe data:...).
     */
    fileToBase64(file) {
      return new Promise((resolve, reject) => {
        const reader = new FileReader()
        reader.onload = () => resolve(reader.result.split(',')[1])
        reader.onerror = reject
        reader.readAsDataURL(file)
      })
    },

    /**
     * Lookup MSPList par FINESS avec token de sequence pour eviter les race
     * conditions (saisie rapide = plusieurs requetes concurrentes, la derniere
     * reponse n'est pas forcement la derniere saisie). Le token est stocke sur
     * l'instance via created() pour ne pas etre reactif (pas de re-render inutile).
     */
    async lookupMspByFiness(finess) {
      const token = ++this._finessLookupToken
      this.finessLoading = true
      this.mspChecked = false
      try {
        const res = await axios.get(
          `${API_BASE}${server.urls.segur}/msp-by-finess/${finess}`,
          { headers: server.getHeader() }
        )
        if (token !== this._finessLookupToken) return
        const data = res.data?.data
        if (data && data.found && data.tranche) {
          this.mspFound = true
          this.mspRaisonSociale = data.raisonSociale || ''
          this.mspRaisonSocialeFromLookup = !!data.raisonSociale
          this.finessGeo = data.FINESSGEO || ''
          this.effectif = data.effectifRef || null
          this.tranche = data.tranche
        } else {
          this.mspFound = false
          this.mspRaisonSociale = data?.raisonSociale || ''
          this.mspRaisonSocialeFromLookup = !!(data && data.raisonSociale)
          this.finessGeo = data?.FINESSGEO || ''
          this.effectif = data?.effectifRef || null
          this.tranche = null
        }
      } catch (e) {
        if (token !== this._finessLookupToken) return
        this.mspFound = false
        this.mspRaisonSociale = ''
        this.finessGeo = ''
        this.effectif = null
        this.tranche = null
      } finally {
        if (token === this._finessLookupToken) {
          this.mspChecked = true
          this.finessLoading = false
          this.computePricingPreview()
        }
      }
    },

    /**
     * Verifie si une licence saisie manuellement existe en base.
     */
    async checkManualLicence() {
      if (!this.manualLicenceId || this.manualLicenceId.length < 3) {
        this.manualLicenceValid = null
        return
      }
      try {
        const res = await axios.get(
          `${API_BASE}${server.urls.segur}/check-licence/${this.manualLicenceId}`,
          { headers: server.getHeader() }
        )
        this.manualLicenceValid = !!(res.data?.data?.exists)
      } catch {
        this.manualLicenceValid = false
      }
    },

    formatEur(val) {
      if (val == null) return '—'
      return val.toFixed(2).replace('.', ',') + ' €'
    },
    copyNumeroEditeur() {
      if (this.numeroEditeur) {
        navigator.clipboard.writeText(this.numeroEditeur)
        this.showSnackbar('Numero editeur copie', 'info')
      }
    },

    showSnackbar(text, color) {
      this.snackbarText = text
      this.snackbarColor = color || 'success'
      this.snackbar = true
    },

    /**
     * Remet tous les champs du formulaire a leur valeur initiale.
     * Appele quand le RPPS change et n'est plus valide.
     */
    resetForm() {
      this.licences = []
      this.selectedLicence = null
      this.manualLicenceId = ''
      this.manualLicenceValid = null
      this.nom = ''
      this.prenom = ''
      this.telephone = ''
      this.email = ''
      this.adresse = ''
      this.codePostal = ''
      this.ville = ''
      this.finess = ''
      this.finessGeo = ''
      this.effectif = null
      this.tranche = null
      this.finessLoading = false
      this.mspChecked = false
      this.mspFound = false
      this.mspRaisonSociale = ''
      this.mspRaisonSocialeFromLookup = false
      this.backendError = null
      this.typePrestation = null
      this.pricing = null
      this.existingBDC = null
      this.numeroEditeur = null
    }
  }
}
</script>

<style scoped>
.content {
  max-width: 900px;
  margin: 0 auto;
  padding: 16px;
}
</style>
